import React, { ComponentProps } from 'react';
import { asFetchableBlock, useBlockContext, useRefreshBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import { DefaultView, DeleteAction, OptionalEditView, Views } from '../../types/BlockWithViews';
import { useSonTranslation } from '../../translation/useSonTranslation';
import {
  BlockInteractions,
  Views as BlockInteractionsViews,
  DISPLAY_TYPE,
  createButtonObject,
} from '../../components/organisms/BlockInteractions';
import { Icons } from '../../components/atoms/SVG';
import { Namespace } from '../../translation/namespace';
import { isNotNullOrUndefined } from '../../lib/typeGuards';
import { CompositionConfigurationDefaultView } from './views/CompositionConfigurationDefaultView';
import { CompositionConfigurationEditView } from './views/CompositionConfigurationEditView';

type CompositionConfigurationProps = Views<
  DefaultView<CompositionConfigurationDefaultView> & OptionalEditView<CompositionConfigurationEditView> & DeleteAction
>;

function CompositionConfiguration({ views, activeView }: CompositionConfigurationProps) {
  const { t } = useSonTranslation('CompositionConfiguration');
  const { uuid, elementType } = useBlockContext();
  const { refreshCount } = useRefreshBlock();

  const buttons = [
    views.delete &&
      createButtonObject(
        BlockInteractionsViews.DELETE,
        Icons.ERASER,
        `${Namespace.CompositionConfiguration}:erase`,
        DISPLAY_TYPE.MODAL,
        null
      ),
    views.edit &&
      createButtonObject(
        BlockInteractionsViews.EDIT,
        Icons.EDIT_PENCIL,
        `${Namespace.CompositionConfiguration}:edit`,
        DISPLAY_TYPE.COLLAPSIBLE,
        <CompositionConfigurationEditView {...views.edit} />
      ),
  ]
    .filter(isNotNullOrUndefined)
    .reduce((btn, elem) => ({ ...elem, ...btn }), {});

  return (
    <BlockInteractions
      key={refreshCount}
      headline={{
        text: t('headline'),
        visible: true,
        hierarchy: 3,
      }}
      alwaysShowInfo
      views={views}
      activeView={activeView}
      buttons={buttons}
      uuid={uuid}
      elementType={elementType}
    >
      <CompositionConfigurationDefaultView {...views.default} />
    </BlockInteractions>
  );
}

export const CompositionConfigurationBlock = asFetchableBlock(
  CompositionConfiguration,
  BlockType.compositionconfiguration
);
export type CompositionConfigurationBlock = ComponentProps<typeof CompositionConfigurationBlock>;
